import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (e) {
  throw new Error('Invalid Supabase URL format');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // pkce is correct for password flows but we let Supabase pick its own
    // cross-browser storage adapter — never hardcode window.localStorage,
    // which throws in Safari private mode and silently drops the session.
    flowType: 'pkce',
    storageKey: 'perplexity-auth-token'
  },
  global: {
    headers: {
      'x-client-info': 'perplexity-website'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Add health check function
export const checkSupabaseConnection = async () => {
  try {
    const { error } = await supabase.from('settings').select('key').limit(1);
    if (error) {
      console.warn('Supabase connection test failed:', error.message);
      return false;
    }
    if (import.meta.env.DEV) {
      console.log('Supabase connection successful');
    }
    return true;
  } catch (error) {
    console.warn('Supabase connection error:', error);
    return false;
  }
};

// Add function to check what's missing in database
export const checkDatabaseState = async () => {
  try {
    const results = {
      tables: {},
      policies: {},
      functions: {},
      storage: {},
      settings: {}
    };

    // Check tables
    const requiredTables = ['contacts', 'access_requests', 'users', 'consultant_profiles', 'settings', 'pages', 'media'];
    for (const table of requiredTables) {
      try {
        const { error } = await supabase.from(table).select('*').limit(1);
        results.tables[table] = !error;
      } catch (e) {
        results.tables[table] = false;
      }
    }

    // Check storage buckets
    try {
      const { data: buckets } = await supabase.storage.listBuckets();
      results.storage.media = buckets?.some(b => b.id === 'media') || false;
    } catch (e) {
      results.storage.media = false;
    }

    // Check settings
    try {
      const { data: settings } = await supabase.from('settings').select('key');
      const settingKeys = settings?.map(s => s.key) || [];
      results.settings.site_url = settingKeys.includes('site_url');
      results.settings.logo_url = settingKeys.includes('logo_url');
      results.settings.email_confirmation_redirect = settingKeys.includes('email_confirmation_redirect');
    } catch (e) {
      results.settings = { site_url: false, logo_url: false, email_confirmation_redirect: false };
    }

    if (import.meta.env.DEV) {
      console.log('Database state check:', results);
    }

    return results;
  } catch (error) {
    console.error('Database state check failed:', error);
    return null;
  }
};
// Add function to test database setup
export const testDatabaseSetup = async () => {
  try {
    // Test if all required tables exist
    const tables = ['contacts', 'access_requests', 'users', 'consultant_profiles', 'settings', 'pages', 'media'];
    const results = await Promise.all(
      tables.map(async (table) => {
        try {
          const { error } = await supabase.from(table).select('*').limit(1);
          return { table, exists: !error, error: error?.message };
        } catch (e) {
          return { table, exists: false, error: (e as Error).message };
        }
      })
    );
    
    if (import.meta.env.DEV) {
      console.log('Database setup test:', results);
    }
    
    return results;
  } catch (error) {
    console.error('Database setup test failed:', error);
    return [];
  }
};